import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScanFace } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApiError } from "../hooks/useApiError";
import { LangToggle, ThemeToggle } from "../components/controls/Toggles";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";
import { PasswordInput } from "../components/neu/PasswordInput";
import { LoginLinks } from "../features/auth/LoginLinks";

const INPUT = "h-14 sm:h-[56px] px-5 text-xl";
const LABEL = "mb-2 block text-sm font-bold text-foreground/80";

export default function LoginPage() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const describe = useApiError();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await signIn(form.email.trim(), form.password);
      navigate("/", { replace: true });
    } catch (err) { setError(describe(err)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-8 pt-[max(2rem,calc(env(safe-area-inset-top)+1rem))] pb-[max(2rem,calc(env(safe-area-inset-bottom)+1rem))]">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="neu flex h-14 w-14 items-center justify-center rounded-2xl p-0">
              <ScanFace className="h-7 w-7 text-primary" />
            </div>
            <p className="text-2xl font-black tracking-tight">{t("app.name")}</p>
          </div>
          <div className="flex gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </div>

        <NeuCard className="animate-rise-in p-6 sm:p-10">
          <h1 className="text-3xl leading-tight font-black tracking-tight sm:text-4xl">{t("auth.welcome")}</h1>
          <p className="mt-3 text-base text-muted-foreground">{t("auth.subtitle")}</p>
          <form className="mt-8 space-y-5" onSubmit={submit}>
            <div>
              <label className={LABEL} htmlFor="login-email-input">{t("auth.email")}</label>
              <NeuInput
                id="login-email-input"
                data-testid="login-email"
                type="email" dir="ltr" required autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="login-password-input">{t("auth.password")}</label>
              <PasswordInput
                id="login-password-input"
                data-testid="login-password"
                required autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                inputClassName={INPUT}
              />
            </div>
            {error ? (
              <p data-testid="login-error" className="text-sm font-semibold text-destructive">{error}</p>
            ) : null}
            <NeuButton
              data-testid="login-submit"
              variant="primary" type="submit"
              disabled={busy}
              className="w-full py-5 text-base font-bold sm:text-lg"
            >
              {busy ? t("app.loading") : t("auth.signIn")}
            </NeuButton>
            <LoginLinks />
          </form>
        </NeuCard>
      </div>
    </div>
  );
}
